import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { Autocomplete } from '@mui/material';

/* ═══════════════════════════════════════════════════════════════════════════
   PALETA — fonte única de cores do sistema.

   Como funciona, em resumo:

   1. `PALETA_PADRAO` guarda as 4 cores de marca da TriAxis. É o fallback,
      usado enquanto o cadastro da empresa não carrega (ou se não houver
      paleta cadastrada).

   2. As demais cores (hover, gradiente, bordas, tints, cores de gráfico)
      NÃO são cadastradas — são DERIVADAS das 4 acima pelas funções desta
      seção. Isso evita pedir 15 cores ao usuário e garante que tudo
      acompanhe quando ele trocar a cor principal.

   3. `cores` exporta strings `var(--...)`. Use em CSS, styled-components e
      no `sx` do MUI. Trocar a paleta reflete na hora, sem re-render.

   4. `coresHex` exporta os MESMOS valores já resolvidos em hexadecimal.
      Use onde `var()` não funciona:
        · createTheme do MUI  (decomposeColor não entende var)
        · ApexCharts          (faz cálculo de tom sobre o valor)
        · pdf-lib             (precisa de rgb numérico)

   5. `aplicarPaleta()` troca a paleta em tempo de execução: reescreve as
      variáveis CSS e atualiza `coresHex`/`coresGrafico` no lugar.
   ═══════════════════════════════════════════════════════════════════════ */

// ── 1. Paleta padrão (manual de marca TriAxis) ─────────────────────────────
export const PALETA_PADRAO = {
  primaria: '#D16609', // laranja — botões, destaques, títulos
  escura: '#2E2E2E', // chumbo — painéis escuros, texto principal
  neutra: '#696969', // cinza médio — texto secundário
  fundoClaro: '#F2F4F7', // cinza claro — fundo de tela e cartões
};

// ── 2. Funções de derivação (puras, testáveis) ─────────────────────────────

const limitar = (n) => Math.max(0, Math.min(255, Math.round(n)));

export function hexParaRgb(hex) {
  let h = String(hex).trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  // Sem operadores bit a bit: o ESLint do projeto (airbnb) proíbe `no-bitwise`.
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

export function rgbParaHex({ r, g, b }) {
  const p = (v) => limitar(v).toString(16).padStart(2, '0');
  return `#${p(r)}${p(g)}${p(b)}`.toUpperCase();
}

/** Mistura com branco. t=0 devolve a cor original, t=1 devolve branco. */
export function clarear(hex, t) {
  const { r, g, b } = hexParaRgb(hex);
  return rgbParaHex({
    r: r + (255 - r) * t,
    g: g + (255 - g) * t,
    b: b + (255 - b) * t,
  });
}

/** Mistura com preto. t=0 devolve a cor original, t=1 devolve preto. */
export function escurecer(hex, t) {
  const { r, g, b } = hexParaRgb(hex);
  return rgbParaHex({ r: r * (1 - t), g: g * (1 - t), b: b * (1 - t) });
}

/** Luminância relativa (WCAG 2.1) — 0 = preto, 1 = branco. */
export function luminancia(hex) {
  const { r, g, b } = hexParaRgb(hex);
  const canal = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/** Razão de contraste entre duas cores (WCAG). O mínimo legível é 4.5. */
export function contraste(hexA, hexB) {
  const a = luminancia(hexA);
  const b = luminancia(hexB);
  const claro = Math.max(a, b);
  const escuro = Math.min(a, b);
  return (claro + 0.05) / (escuro + 0.05);
}

/** Escolhe texto branco ou escuro para ficar legível sobre `hexFundo`. */
export function textoSobre(hexFundo, hexEscuro = '#2E2E2E') {
  return contraste(hexFundo, '#FFFFFF') >= contraste(hexFundo, hexEscuro)
    ? '#FFFFFF'
    : hexEscuro;
}

/** Rampa sequencial de `n` tons, do mais claro ao mais escuro. */
export function rampa(hex, n = 7) {
  const meio = Math.floor(n / 2);
  return Array.from({ length: n }, (_, i) => {
    if (i === meio) return String(hex).toUpperCase();
    if (i < meio) return clarear(hex, ((meio - i) / (meio + 1)) * 0.85);
    return escurecer(hex, ((i - meio) / (n - meio)) * 0.75);
  });
}

/** Gira o matiz em `graus` — usado para gerar cores complementares de gráfico. */
export function girarMatiz(hex, graus) {
  const { r, g, b } = hexParaRgb(hex);
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
  }
  h = ((h * 60) + graus + 360) % 360;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const faixa = [
    [c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x],
  ][Math.floor(h / 60) % 6];
  return rgbParaHex({
    r: (faixa[0] + m) * 255, g: (faixa[1] + m) * 255, b: (faixa[2] + m) * 255,
  });
}

// ── 3. Derivação completa a partir das 4 cores de marca ────────────────────

/**
 * Recebe as 4 cores cadastradas e devolve todos os tokens do sistema.
 * Alterar as regras aqui reflete em todas as telas de uma vez.
 */
export function derivarTokens(paleta = PALETA_PADRAO) {
  const p = { ...PALETA_PADRAO, ...paleta };
  const { primaria, escura, neutra, fundoClaro } = p;

  return {
    // marca
    laranja: primaria,
    laranjaEscuro: escurecer(primaria, 0.20), // hover de botões
    laranjaClaro: clarear(primaria, 0.90), // fundo de cabeçalho de tabela, chips
    chumbo: escura,
    cinzaMedio: neutra,
    cinzaClaro: fundoClaro,
    branco: '#FFFFFF',

    // painel escuro dos modais (gradiente)
    painelTopo: clarear(escura, 0.08),
    painelBase: escura,

    // texto
    textoClaro: '#FFFFFF',
    textoSobrePrimaria: textoSobre(primaria, escura),

    // neutros de interface — bordas, hovers e divisores.
    // Não são cadastrados: saem da cor escura, clareada em graus diferentes.
    borda: clarear(escura, 0.80),
    bordaSuave: clarear(escura, 0.88),
    hoverSuave: clarear(escura, 0.92),
  };
}

/** Paleta de gráficos: 3 cores de marca + 3 complementares por rotação de matiz. */
export function derivarCoresGrafico(paleta = PALETA_PADRAO) {
  const p = { ...PALETA_PADRAO, ...paleta };
  return [
    p.primaria,
    p.escura,
    p.neutra,
    clarear(p.primaria, 0.35),
    girarMatiz(p.primaria, 170),
    girarMatiz(p.primaria, 100),
  ];
}

// ── 4. Nomes das variáveis CSS ─────────────────────────────────────────────
// A chave é o nome do token; o valor é a variável CSS correspondente.
const VAR = {
  laranja: '--marca-primaria',
  laranjaEscuro: '--marca-primaria-escura',
  laranjaClaro: '--marca-primaria-clara',
  chumbo: '--marca-escura',
  cinzaMedio: '--marca-neutra',
  cinzaClaro: '--marca-fundo-claro',
  branco: '--branco',
  painelTopo: '--painel-topo',
  painelBase: '--painel-base',
  textoClaro: '--texto-claro',
  textoSobrePrimaria: '--texto-sobre-primaria',
  borda: '--borda',
  bordaSuave: '--borda-suave',
  hoverSuave: '--hover-suave',
};

/**
 * `cores` — use em CSS, styled-components e no `sx` do MUI.
 * Cada valor é uma referência a variável CSS, então trocar a paleta
 * atualiza a tela inteira sem re-render.
 *
 * NÃO use aqui: createTheme do MUI, ApexCharts, pdf-lib. Para esses,
 * use `coresHex` (abaixo).
 */
const coresVar = {};
Object.keys(VAR).forEach((chave) => {
  coresVar[chave] = `var(${VAR[chave]})`;
});
export const cores = coresVar;

/**
 * `coresHex` — os mesmos tokens, já resolvidos em hexadecimal.
 * É um objeto vivo: `aplicarPaleta()` atualiza as chaves no lugar,
 * então quem ler depois da troca pega o valor novo.
 */
export const coresHex = { ...derivarTokens(PALETA_PADRAO) };

/**
 * `coresGrafico` — paleta de séries para gráficos. Array vivo (mutado no
 * lugar por `aplicarPaleta`), sempre em hexadecimal.
 */
export const coresGrafico = [...derivarCoresGrafico(PALETA_PADRAO)];

/** Componentes r,g,b da cor primária — para montar rgba() com opacidade. */
export const VAR_PRIMARIA_RGB = '--marca-primaria-rgb';

// ── 5. Aplicação em tempo de execução ──────────────────────────────────────

/**
 * Troca a paleta do sistema. Chame no boot, depois de buscar a empresa:
 *
 *   const empresa = await getEmpresaAtual();
 *   if (empresa?.paleta) aplicarPaleta(empresa.paleta);
 *
 * `paleta` aceita { primaria, escura, neutra, fundoClaro } — campos
 * ausentes caem no padrão TriAxis.
 *
 * Observação: os gráficos leem `coresGrafico` no momento em que renderizam.
 * Se a paleta mudar com um gráfico já na tela (ex.: pré-visualização na
 * tela de configuração), remonte o gráfico trocando a prop `key`.
 */
export function aplicarPaleta(paleta = PALETA_PADRAO) {
  const tokens = derivarTokens(paleta);
  const grafico = derivarCoresGrafico(paleta);

  if (typeof document !== 'undefined') {
    const raiz = document.documentElement;
    Object.keys(VAR).forEach((chave) => {
      raiz.style.setProperty(VAR[chave], tokens[chave]);
    });
    const { r, g, b } = hexParaRgb(tokens.laranja);
    raiz.style.setProperty(VAR_PRIMARIA_RGB, `${r}, ${g}, ${b}`);
  }

  // objetos vivos — atualizados no lugar para não quebrar quem já importou
  Object.assign(coresHex, tokens);
  coresGrafico.splice(0, coresGrafico.length, ...grafico);

  return tokens;
}

// Aplica os padrões TriAxis assim que este módulo é carregado.
//
// Por que aqui, e não só no `:root` do globalStyles: todo token de `cores` é
// um `var(--...)`. Se a variável não existir, a declaração CSS inteira vira
// inválida e o navegador a DESCARTA — o fundo some, o botão some, a cor do
// texto volta ao padrão. Depender do <GlobalStyle /> estar montado tornaria
// o sistema frágil a uma linha esquecida no App.js.
//
// Como todo componente estilizado importa este módulo, esta chamada roda
// antes de qualquer render. `aplicarPaleta()` grava as variáveis via style
// inline no <html>, que tem precedência sobre o :root.
aplicarPaleta(PALETA_PADRAO);

/** Mapa { variavelCSS: valor } dos padrões — usado pelo globalStyles. */
export function variaveisPadrao() {
  const tokens = derivarTokens(PALETA_PADRAO);
  const { r, g, b } = hexParaRgb(tokens.laranja);
  const linhas = Object.keys(VAR).map((c) => `${VAR[c]}: ${tokens[c]};`);
  linhas.push(`${VAR_PRIMARIA_RGB}: ${r}, ${g}, ${b};`);
  return linhas.join('\n    ');
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENTES ESTILIZADOS
   ═══════════════════════════════════════════════════════════════════════ */

// DIV
export const RowContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  align-items: left;
`;

export const RowContainerTable = styled.div`
  display: flex;
  gap: 3px;
  padding: 8px;
  margin-bottom: 15px;
  align-items: center;
  background-color: ${cores.cinzaMedio};
  border-radius: 8px;
  height: 50px;

  /* Estilo padrão para telas maiores (3 parcelas por linha) */
  width: calc(33.33% - 6px); /* Aproximadamente 1/3 da largura, considerando o gap */
  box-sizing: border-box; /* Garante que padding e border sejam incluídos na largura */

  /* Media query para telas menores (2 parcelas por linha) */
  @media (max-width: 1900px) { /* Você pode ajustar este breakpoint conforme necessário */
    width: calc(50% - 4.5px); /* Aproximadamente 1/2 da largura, considerando o gap */
  }

  /* Media query para telas muito pequenas, se necessário, para 1 parcela por linha */
  @media (max-width: 480px) {
    width: calc(100% - 3px); /* 100% da largura para uma única parcela por linha */
  }
`;

export const RadioGroup = styled.div`
  display: flex;
  align-items: center;
`;

export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%);
  border-top: 4px solid ${cores.laranja};
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 95%;
  height: auto;
  max-height: 95%;
  min-height: 50%;
  margin: 20px auto;
  font-family: sans-serif; /* Add a default font */
  overflow-y: auto;
  overflow-x: hidden;
  @media (max-width: 768px) {
    width: 95%;
    padding: 15px;
  }
`;

// H
export const Label = styled.h1`
  font-size: 0.9em;
  color: ${cores.textoClaro};
  margin-bottom: 5px;
  text-align: left;
`;

export const BaseLabel = styled.h1`
  font-size: 0.9em;
  color: ${cores.textoClaro};
  margin-bottom: 5px;
  text-align: left;
  width: 180px;
  max-width: 300px;
  min-width: 180px;
`;

export const SectionTitle = styled.h2`
  font-size: 1em;
  color: ${cores.textoClaro};
  margin-top: 15px;
  margin-bottom: 10px;
  padding-bottom: 10px;
  text-align: center;
`;

// Label
export const RadioLabel = styled.label`
  font-size: 0.9em;
  color: ${cores.textoClaro};
  font-weight: bold;
  line-height: 10px;
`;

// Button — ação genérica; segue a cor primária da marca (regra 2 do padrão visual)
export const ImportButton = styled.button`
  background-color: ${cores.laranja};
  color: ${cores.textoSobrePrimaria};
  border: none;
  border-radius: 5px;
  padding: 8px 15px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: ${cores.laranjaEscuro};
  }
`;

// Button — variante neutra, para quando há dois botões lado a lado
export const ImportButtonBlue = styled.button`
  background-color: ${cores.chumbo};
  color: ${cores.textoClaro};
  border: none;
  border-radius: 5px;
  padding: 8px 15px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: ${cores.cinzaMedio};
  }
`;

// Input
const baseInput = `
  padding: 8px;
  border: 1px solid ${cores.borda};
  border-radius: 15px;
  font-size: 14px;
  box-sizing: border-box;
`;

const focoInput = `
  &:focus {
    border-color: ${cores.laranja};
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(var(${VAR_PRIMARIA_RGB}), 0.25);
  }
`;

export const Input = styled.input`
  ${baseInput}
  width: 100%;
  ${focoInput}
`;

export const InputHalf = styled.input`
  ${baseInput}
  width: 30%;

  @media (max-width: 768px) {
    width: 50%;
  }
  ${focoInput}
`;

export const InputSmall = styled.input`
  ${baseInput}
  width: 20%;

  @media (max-width: 768px) {
    width: 20%;
  }
  ${focoInput}
`;

export const InputCode = styled.input`
  ${baseInput}
  width: 20%;
  text-align: center;
  @media (max-width: 768px) {
    width: 10%;
  }
  ${focoInput}
`;

export const InputCodeSmall = styled.input`
  ${baseInput}
  width: 100px;
  text-align: center;
  @media (max-width: 768px) {
    width: 100px;
  }
  ${focoInput}
`;

export const RadioInput = styled.input`
  margin-right: 5px;
`;

export const Checkbox = styled.input`
  margin-right: 5px;
`;

// Select
export const Select = styled.select`
  padding: 8px;
  border: 1px solid ${cores.borda};
  border-radius: 10px;
  font-size: 14px;
  width: 20%;
  box-sizing: border-box;
`;

// Autocomplete
// (o valor de fundo era '#ffffffff' — 8 dígitos, bug de digitação: o último
//  'f' virava canal alfa. Corrigido para o token de branco.)
export const AutoComplete = muiStyled(Autocomplete)`
  width: 70%;
  border-radius: 10px;
  box-sizing: border-box;
  background-color: ${cores.branco};
  & .MuiInputBase-root {
    height: 36px;
    padding: 1.5px;
  }
`;

export const AutoCompleteMedium = muiStyled(Autocomplete)`
  border-radius: 10px;
  width: 50%;
  box-sizing: border-box;
  background-color: ${cores.branco};
  & .MuiInputBase-root {
    height: 36px;
    padding: 1.5px;
  }
`;

export const AutoCompleteSmall = muiStyled(Autocomplete)`
  border-radius: 10px;
  width: 20%;
  box-sizing: border-box;
  background-color: ${cores.branco};
  & .MuiInputBase-root {
    height: 36px;
    padding: 1.5px;
  }
`;

// Estilo compartilhado de campo escuro: label branco "acima" da caixa,
// sem entalhe na borda. Usar com spread no sx do campo:
//   sx={{ width: '300px', backgroundColor: 'white', ...labelCampoEscuro }}
// Centraliza o padrão — para ajustar o label em todo o sistema, edite só aqui.
// raioCampo — token de borda arredondada dos campos de formulário (Tópico
// "Melhorias Visuais" v1, item 2). Antes não existia nenhum borderRadius
// pra inputs (só pra cards/modais); centralizado aqui pra virar consistente
// em todo o sistema com uma linha só.
export const raioCampo = '12px';

export const labelCampoEscuro = {
  mt: '18px',
  // Arredonda o container inteiro também — sem isso, o fundo branco do
  // wrapper (aplicado por campoEscuro()) fica quadrado por baixo do input
  // arredondado e "vaza" nos cantos (o recorte retangular atrás do campo).
  borderRadius: raioCampo,
  '& .MuiInputLabel-root': { color: cores.textoClaro, fontWeight: 500, transform: 'translate(2px, -22px) scale(0.85)', maxWidth: '100%' },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': { color: cores.textoClaro, fontWeight: 500, transform: 'translate(2px, -22px) scale(0.85)' },
  '& .MuiInputLabel-root.Mui-disabled': { color: cores.textoClaro },
  '& .MuiInputLabel-root.Mui-focused': { color: cores.textoClaro },
  '& .MuiOutlinedInput-notchedOutline': { top: 0, borderRadius: raioCampo },
  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
  '& .MuiOutlinedInput-root': { borderRadius: raioCampo },
};

/**
 * Atalho para campo de texto sobre painel escuro: já traz fundo branco e o
 * label claro acima da caixa. Evita ter que lembrar do `...labelCampoEscuro`
 * em cada campo novo — foi a causa de um defeito de contraste recorrente.
 *
 *   <TextField label="Nome" sx={campoEscuro('70%')} />
 */
export const campoEscuro = (largura = '70%') => ({
  width: largura,
  backgroundColor: cores.branco,
  ...labelCampoEscuro,
});